import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Send, Users } from "lucide-react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Avatar } from "@/components/ui/Avatar";
import {
  usePreviewChangelogBroadcast,
  useSendChangelogBroadcast,
  type ChangelogRecipient,
} from "@/lib/data";
import { buildChangelogEmail } from "@/lib/changelog-email";
import type { ChangelogEntry } from "@/lib/changelog";

// =============================================================================
// ChangelogBroadcastDialog
// -----------------------------------------------------------------------------
// Popup intermédiaire entre le bouton "Envoyer aux admins" et l'envoi effectif :
//   1. À l'ouverture, charge la liste des destinataires + le HTML rendu via
//      l'edge function `changelog-broadcast` en mode dry_run.
//   2. Affiche les destinataires (avatar + nom + email) et l'aperçu HTML
//      dans une iframe sandboxée.
//   3. Bouton "Envoyer" qui appelle la même edge function en mode envoi réel,
//      puis affiche un récapitulatif (X envoyés / Y échoués).
// =============================================================================

interface ChangelogBroadcastDialogProps {
  open: boolean;
  onClose: () => void;
  entry: ChangelogEntry | null;
}

export function ChangelogBroadcastDialog({ open, onClose, entry }: ChangelogBroadcastDialogProps) {
  const previewMutation = usePreviewChangelogBroadcast();
  const sendMutation = useSendChangelogBroadcast();

  const [previewData, setPreviewData] = useState<{
    recipients: ChangelogRecipient[];
    subject: string;
    html: string;
  } | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [sendResult, setSendResult] = useState<{
    sent: number;
    failed: number;
    total: number;
    failedRecipients: Array<{ email: string; reason: string }>;
  } | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  // Build le subject/html/text une fois à l'ouverture (memo)
  const builtEmail = useMemo(() => {
    if (!entry) return null;
    return buildChangelogEmail(entry, { appUrl: window.location.origin });
  }, [entry]);

  // À l'ouverture du dialog : reset + lance la preview (dry_run)
  useEffect(() => {
    if (!open || !entry || !builtEmail) return;
    setPreviewData(null);
    setPreviewError(null);
    setSendResult(null);
    setSendError(null);
    previewMutation
      .mutateAsync({
        version: entry.version,
        subject: builtEmail.subject,
        html: builtEmail.html,
        text: builtEmail.text,
      })
      .then(setPreviewData)
      .catch((e: unknown) => {
        setPreviewError(e instanceof Error ? e.message : "Erreur lors de la prévisualisation");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entry?.version]);

  async function handleSend() {
    if (!entry || !builtEmail) return;
    setSendError(null);
    try {
      const res = await sendMutation.mutateAsync({
        version: entry.version,
        subject: builtEmail.subject,
        html: builtEmail.html,
        text: builtEmail.text,
      });
      setSendResult({
        sent: res.sent,
        failed: res.failed,
        total: res.total,
        failedRecipients: res.failedRecipients,
      });
    } catch (e) {
      setSendError(e instanceof Error ? e.message : "Erreur lors de l'envoi");
    }
  }

  const isLoadingPreview = previewMutation.isPending;
  const isSending = sendMutation.isPending;
  const isDone = sendResult !== null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="2xl"
      blocking={isSending}
      level={2}
      title={
        isDone ? (
          "Envoi terminé"
        ) : (
          <span className="flex items-center gap-2">
            <Send className="size-5 text-brand" />
            Notifier les administrateurs
          </span>
        )
      }
      description={
        isDone
          ? undefined
          : entry
            ? `Aperçu de l'email pour la version v${entry.version}.`
            : undefined
      }
      footer={
        isDone ? (
          <Button onClick={onClose}>Fermer</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={isSending}>
              Annuler
            </Button>
            <Button
              variant="primary"
              onClick={() => void handleSend()}
              loading={isSending}
              disabled={
                isLoadingPreview ||
                Boolean(previewError) ||
                !previewData ||
                previewData.recipients.length === 0
              }
              iconLeft={!isSending ? <Send className="size-4" /> : undefined}
            >
              Envoyer à {previewData?.recipients.length ?? 0} admin
              {(previewData?.recipients.length ?? 0) > 1 ? "s" : ""}
            </Button>
          </>
        )
      }
    >
      {/* État final : récap d'envoi */}
      {isDone && sendResult && (
        <div className="space-y-4">
          {sendResult.sent > 0 && (
            <div
              role="status"
              className="flex items-start gap-3 rounded-lg border border-success/30 bg-success/10 p-4"
            >
              <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-success" />
              <p className="text-sm text-success">
                <strong>
                  {sendResult.sent} email{sendResult.sent > 1 ? "s" : ""} envoyé
                  {sendResult.sent > 1 ? "s" : ""}
                </strong>{" "}
                sur {sendResult.total}.
              </p>
            </div>
          )}

          {sendResult.failed > 0 && (
            <div className="rounded-lg border border-danger/30 bg-danger/10 p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 size-5 shrink-0 text-danger" />
                <p className="text-sm text-danger">
                  <strong>
                    {sendResult.failed} échec{sendResult.failed > 1 ? "s" : ""}
                  </strong>{" "}
                  sur {sendResult.total}. Détail&nbsp;:
                </p>
              </div>
              <ul className="mt-3 space-y-2 pl-8">
                {sendResult.failedRecipients.map((f, i) => (
                  <li key={i} className="text-xs">
                    <code className="rounded bg-danger/15 px-1.5 py-0.5 text-danger">
                      {f.email}
                    </code>
                    <span className="ml-2 text-danger/80">{f.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {sendResult.sent === 0 && sendResult.failed === 0 && (
            <p className="rounded-lg border border-dashed border-line p-4 text-center text-sm text-ink-muted">
              Aucun email traité.
            </p>
          )}
        </div>
      )}

      {/* État erreur d'envoi (avant l'écran final) */}
      {!isDone && sendError && (
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-danger/30 bg-danger/10 p-3">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-danger" />
          <p className="text-sm text-danger">{sendError}</p>
        </div>
      )}

      {/* Loading preview */}
      {!isDone && isLoadingPreview && (
        <div className="flex items-center justify-center gap-2 rounded-lg border border-line bg-bg-subtle p-12 text-sm text-ink-muted">
          <Loader2 className="size-4 animate-spin" />
          Chargement des destinataires…
        </div>
      )}

      {/* Erreur preview */}
      {!isDone && !isLoadingPreview && previewError && (
        <div className="flex items-start gap-3 rounded-lg border border-danger/30 bg-danger/10 p-4">
          <AlertTriangle className="mt-0.5 size-5 shrink-0 text-danger" />
          <div>
            <p className="font-medium text-danger">Impossible de préparer l'envoi</p>
            <p className="mt-1 text-xs text-danger/80">{previewError}</p>
          </div>
        </div>
      )}

      {/* Contenu principal : destinataires + iframe */}
      {!isDone && !isLoadingPreview && previewData && (
        <div className="space-y-4">
          {/* En-tête méta (Sujet) */}
          <div className="space-y-1.5 rounded-lg border border-line bg-bg-subtle px-3 py-2 text-xs">
            <div className="flex gap-2">
              <span className="w-16 shrink-0 font-medium uppercase tracking-wider text-ink-subtle">
                Sujet
              </span>
              <span className="truncate font-medium text-ink">{previewData.subject}</span>
            </div>
          </div>

          {/* Liste destinataires */}
          <RecipientsList recipients={previewData.recipients} />

          {/* Aperçu HTML */}
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wider text-ink-subtle">
              Aperçu de l'email
            </p>
            <div className="rounded-lg border border-line bg-white">
              <iframe
                title="Aperçu changelog"
                srcDoc={previewData.html}
                sandbox=""
                className="h-[480px] w-full rounded-lg border-0"
              />
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
function RecipientsList({ recipients }: { recipients: ChangelogRecipient[] }) {
  return (
    <div>
      <p className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-ink-subtle">
        <Users className="size-3.5" />
        Destinataires ({recipients.length})
      </p>
      {recipients.length === 0 ? (
        <p className="rounded-lg border border-dashed border-line p-4 text-center text-xs text-ink-muted">
          Aucun administrateur avec un email valide n'a été trouvé.
        </p>
      ) : (
        <ul className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-line bg-bg-subtle/30 p-2">
          {recipients.map((r) => {
            const name = [r.prenom, r.nom].filter(Boolean).join(" ").trim() || r.email;
            return (
              <li
                key={r.user_id}
                className="flex items-center gap-2.5 rounded-md bg-bg px-2.5 py-1.5"
              >
                <Avatar name={name} size="sm" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-ink">{name}</div>
                  <div className="truncate text-xs text-ink-muted">{r.email}</div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
